import { ReusablePhaseEditorModal } from "@/components/features/settings/defaults/matter-phase-projects/ReusablePhaseEditorModal";
import {
  ActionConfirmationModal,
  Badge,
  Button,
  Card,
  CardContent,
  CardTitle,
  Input,
  LoadingSpinner,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Text,
} from "@/components/ui";
import { useUser } from "@/components/UserProvider";
import { useDebouncedValue } from "@/hooks/useDebouncedValue";
import {
  reusablePhasesQueryOptions,
  useArchiveReusablePhase,
  useDeleteReusablePhase,
} from "@/lib/data/react-query/matters/matter-phase";
import { MATTER_PHASE_PERMISSION_KEYS } from "@/lib/data/static/permissionOptions";
import type {
  ReusableMatterPhase,
  ReusableMatterPhaseLifecycle,
} from "@/lib/types";
import { useQuery } from "@tanstack/react-query";
import { Archive, Pencil, Plus, Search, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";

const PAGE_SIZE = 12;

export function PhaseRegistryView() {
  const { hasGrant } = useUser();
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 250).trim();
  const [lifecycle, setLifecycle] = useState<
    ReusableMatterPhaseLifecycle | "All"
  >("Active");
  const [pageNumber, setPageNumber] = useState(1);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editingPhase, setEditingPhase] = useState<ReusableMatterPhase>();
  const [pendingAction, setPendingAction] = useState<{
    type: "archive" | "delete";
    phase: ReusableMatterPhase;
  }>();
  const canCreate = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.reusablePhase,
    "create",
  );
  const canUpdate = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.reusablePhase,
    "update",
  );
  const canDelete = hasGrant(
    MATTER_PHASE_PERMISSION_KEYS.reusablePhase,
    "delete",
  );
  const archivePhase = useArchiveReusablePhase();
  const deletePhase = useDeleteReusablePhase();
  const { data, isLoading, isFetching, isError } = useQuery(
    reusablePhasesQueryOptions({
      q: debouncedSearch || undefined,
      lifecycle: lifecycle === "All" ? undefined : lifecycle,
      pageNumber,
      pageSize: PAGE_SIZE,
    }),
  );

  useEffect(() => {
    setPageNumber(1);
  }, [debouncedSearch, lifecycle]);

  const count = data?.pagination.count ?? 0;
  const pages = Math.max(1, Math.ceil(count / PAGE_SIZE));

  return (
    <div className="space-y-5">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <Text variant="cardTitle">Reusable phases</Text>
        </div>
        {canCreate ? (
          <Button
            onClick={() => {
              setEditingPhase(undefined);
              setEditorOpen(true);
            }}
          >
            <Plus className="mr-2 h-4 w-4" /> Create phase
          </Button>
        ) : null}
      </div>

      <div className="border-border/60 bg-muted/30 flex flex-col gap-3 rounded-lg border p-4 sm:flex-row">
        <div className="relative flex-1">
          <Search className="text-muted-foreground absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2" />
          <Input
            value={search}
            onChange={(event) => {
              setSearch(event.target.value);
            }}
            className="bg-background pl-9"
            placeholder="Search reusable phases"
          />
        </div>
        <Select
          value={lifecycle}
          onValueChange={(value) => {
            setLifecycle(value as ReusableMatterPhaseLifecycle | "All");
          }}
        >
          <SelectTrigger className="bg-background sm:w-48">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="All">All lifecycles</SelectItem>
            <SelectItem value="Active">Active</SelectItem>
            <SelectItem value="Archived">Archived</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {isLoading ? (
        <div className="flex justify-center py-16">
          <LoadingSpinner className="h-6 w-6" />
        </div>
      ) : isError ? (
        <Text variant="subHeading">
          Reusable phases could not be loaded. Please try again.
        </Text>
      ) : data?.data.length ? (
        <div className="relative">
          {isFetching ? (
            <div className="bg-background/50 absolute inset-0 z-10 flex items-center justify-center rounded-lg backdrop-blur-[1px]">
              <LoadingSpinner className="h-6 w-6" />
            </div>
          ) : null}
          <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {data.data.map((phase) => (
              <Card key={phase.id} className="overflow-hidden shadow-sm">
                <CardTitle className="bg-light-primary flex items-start justify-between gap-3 p-3">
                  <span className="min-w-0 truncate text-base">
                    {phase.title}
                  </span>
                  <Badge
                    variant="outline"
                    className="bg-background shrink-0 font-normal"
                  >
                    {phase.lifecycle}
                  </Badge>
                </CardTitle>
                <CardContent className="space-y-4 p-4">
                  <Text variant="caption" className="min-h-10">
                    {phase.description || "No description provided."}
                  </Text>
                  {phase.lifecycle === "Active" && (canUpdate || canDelete) ? (
                    <div className="flex flex-wrap gap-2">
                      {canUpdate ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setEditingPhase(phase);
                            setEditorOpen(true);
                          }}
                        >
                          <Pencil className="mr-2 h-3.5 w-3.5" /> Edit
                        </Button>
                      ) : null}
                      {canUpdate ? (
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => {
                            setPendingAction({ type: "archive", phase });
                          }}
                        >
                          <Archive className="mr-2 h-3.5 w-3.5" /> Archive
                        </Button>
                      ) : null}
                      {canDelete ? (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => {
                            setPendingAction({ type: "delete", phase });
                          }}
                        >
                          <Trash2 className="text-destructive mr-2 h-3.5 w-3.5" />
                          Delete
                        </Button>
                      ) : null}
                    </div>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      ) : (
        <Card className="border-border/60 border-dashed">
          <CardContent className="py-12 text-center">
            <Text variant="body">No reusable phases found</Text>
            <Text variant="caption">
              Adjust the filters or create a new reusable phase.
            </Text>
          </CardContent>
        </Card>
      )}

      {count > 0 ? (
        <div className="flex items-center justify-between">
          <Text variant="caption">
            Showing {(pageNumber - 1) * PAGE_SIZE + 1}–
            {Math.min(pageNumber * PAGE_SIZE, count)} of {count}
          </Text>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={pageNumber === 1}
              onClick={() => {
                setPageNumber((page) => page - 1);
              }}
            >
              Previous
            </Button>
            <Button
              variant="outline"
              size="sm"
              disabled={pageNumber >= pages}
              onClick={() => {
                setPageNumber((page) => page + 1);
              }}
            >
              Next
            </Button>
          </div>
        </div>
      ) : null}

      <ReusablePhaseEditorModal
        open={editorOpen}
        onOpenChange={setEditorOpen}
        phase={editingPhase}
      />
      <ActionConfirmationModal
        open={Boolean(pendingAction)}
        setOpen={(open) => {
          if (!open) setPendingAction(undefined);
        }}
        title={
          pendingAction?.type === "archive"
            ? `Archive ${pendingAction.phase.title}?`
            : `Delete ${pendingAction?.phase.title ?? "this phase"}?`
        }
        description={
          pendingAction?.type === "archive"
            ? "Archiving is permanent. The Phase becomes read-only and cannot be added to Projects."
            : "A Phase can only be deleted when it has never been used in a Project."
        }
        buttonText={pendingAction?.type === "archive" ? "Archive" : "Delete"}
        isActionPending={archivePhase.isPending || deletePhase.isPending}
        afterActionFn={async () => {
          if (!pendingAction) return;
          const variables = {
            reusablePhaseId: pendingAction.phase.id,
            dateModified: pendingAction.phase.dateModified,
          };
          if (pendingAction.type === "archive") {
            await archivePhase.mutateAsync(variables);
          } else {
            await deletePhase.mutateAsync(variables);
          }
          setPendingAction(undefined);
        }}
      />
    </div>
  );
}
